import { isEmpty } from 'lodash';
import {
  IntervalVariableSpec,
  ListVariableSpec,
  PrometheusLabelValuesVariableOptions,
  TextVariableDefinition,
  VariableDefinition,
} from '@/core';

/** Runtime check for list variable plugin spec shape (avoids unsafe casts in the editor UI). */
export function isPrometheusLabelValuesVariableOptions(spec: unknown): spec is PrometheusLabelValuesVariableOptions {
  if (typeof spec !== 'object' || spec === null) {
    return false;
  }
  const { label_name, matchers } = spec as Record<string, unknown>;
  if (typeof label_name !== 'string') {
    return false;
  }
  if (matchers !== undefined) {
    if (!Array.isArray(matchers) || !matchers.every((m) => typeof m === 'string')) {
      return false;
    }
  }
  return true;
}

export function getInitialState(initialVariableDefinition: VariableDefinition) {
  const textVariableFields = {
    value: (initialVariableDefinition as TextVariableDefinition).spec.value ?? '',
  };

  const listVariableFields: {
    allowMultiple: boolean;
    allowAll: boolean;
    capturing_regexp: string | undefined;
    plugin: {
      kind: string;
      spec: Record<string, unknown>;
    };
    refresh: ListVariableSpec['refresh'];
    customAllValue: string | undefined;
    sort: ListVariableSpec['sort'] | undefined;
    default_value: string | string[] | null | undefined;
  } = {
    allowMultiple: false,
    allowAll: false,
    capturing_regexp: undefined,
    plugin: {
      kind: '',
      spec: {},
    },
    refresh: 'DashboardLoad',
    customAllValue: undefined,
    sort: undefined,
    default_value: undefined,
  };

  if (initialVariableDefinition.kind === 'ListVariable') {
    listVariableFields.allowMultiple = initialVariableDefinition.spec.allow_multiple ?? false;
    listVariableFields.allowAll = initialVariableDefinition.spec.allow_all_value ?? false;
    listVariableFields.capturing_regexp = initialVariableDefinition.spec.capturing_regexp;
    listVariableFields.plugin = initialVariableDefinition.spec.plugin;
    listVariableFields.customAllValue = initialVariableDefinition.spec.custom_all_value;
    listVariableFields.refresh = initialVariableDefinition.spec.refresh ?? 'DashboardLoad';
    listVariableFields.sort = initialVariableDefinition.spec.sort;
    listVariableFields.default_value = initialVariableDefinition.spec.default_value;
  }

  const intervalVariableFields: {
    auto: boolean;
    auto_min_interval: IntervalVariableSpec['auto_min_interval'];
    auto_interval_size: IntervalVariableSpec['auto_interval_size'];
    options: string[];
  } = {
    auto: true,
    auto_min_interval: undefined,
    // Default to 'Large' for new interval variables to help prevent rate()/increase() 'No data' issues.
    // This only applies when creating a new variable, not editing existing ones.
    auto_interval_size: 'Large',
    options: [],
  };
  if (initialVariableDefinition.kind === 'IntervalVariable') {
    // Editing an existing IntervalVariable - preserve all existing values
    intervalVariableFields.auto = initialVariableDefinition.spec.auto ?? false;
    intervalVariableFields.auto_min_interval = initialVariableDefinition.spec.auto_min_interval ?? '';
    // Preserve undefined for backwards compatibility - runtime defaults to 'Medium'
    intervalVariableFields.auto_interval_size = initialVariableDefinition.spec.auto_interval_size;
    intervalVariableFields.options = initialVariableDefinition.spec.options ?? [];
  }

  return {
    name: initialVariableDefinition.spec.name,
    title: initialVariableDefinition.spec.display?.name,
    kind: initialVariableDefinition.kind,
    description: initialVariableDefinition.spec.display?.description,
    hidden: initialVariableDefinition.spec.display?.hidden,
    listVariableFields,
    textVariableFields,
    intervalVariableFields,
  };
}

export type VariableEditorState = ReturnType<typeof getInitialState>;

export function getVariableDefinitionFromState(state: VariableEditorState): VariableDefinition {
  const { name, title, kind, description, hidden } = state;

  let display: VariableDefinition['spec']['display'] = {
    name: title || name,
    description: isEmpty(description) ? undefined : description,
    ...(hidden !== undefined && { hidden }),
  };

  // Omit the display property if none of the properties are set
  const omitDisplayProperty = isEmpty(description) && isEmpty(hidden) && isEmpty(title);
  if (omitDisplayProperty) {
    display = undefined;
  }

  if (kind === 'TextVariable') {
    return {
      kind,
      spec: {
        name,
        display,
        ...state.textVariableFields,
      },
    };
  }

  if (kind === 'ListVariable') {
    return {
      kind,
      spec: {
        name,
        display,
        allow_multiple: state.listVariableFields.allowMultiple,
        allow_all_value: state.listVariableFields.allowAll,
        capturing_regexp: state.listVariableFields.capturing_regexp,
        plugin: state.listVariableFields.plugin,
        custom_all_value: state.listVariableFields.customAllValue,
        refresh: state.listVariableFields.refresh,
        sort: state.listVariableFields.sort,
        default_value: state.listVariableFields.default_value,
      },
    };
  }

  if (kind === 'IntervalVariable') {
    return {
      kind,
      spec: {
        name,
        display,
        auto: state.intervalVariableFields.auto,
        auto_min_interval: state.intervalVariableFields.auto_min_interval ?? undefined,
        auto_interval_size: state.intervalVariableFields.auto_interval_size,
        options: state.intervalVariableFields.options.length > 0 ? state.intervalVariableFields.options : undefined,
      },
    };
  }
  throw new Error(`Unknown variable kind: ${kind}`);
}
